/**
 * AI チャット（/api/chat/*）の定数
 *
 * 値の SSOT はバックエンドの `backend/src/common/models_chat.py`。
 * 設計: docs/decisions/ai-chat-architecture.md
 */

/** 1 回の発言の最大文字数（ChatMessageRequest.message） */
export const CHAT_MESSAGE_MAX_LENGTH = 1000;

export const CHAT_ENTRY_ROLE = {
  USER: 'user',
  ASSISTANT: 'assistant',
  /** サーバーからのお知らせ（確定結果・時間切れなど） */
  NOTICE: 'notice',
} as const;

export const CHAT_ACTION_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
  FAILED: 'failed',
} as const;

export const CHAT_ACTION_STATUS_LABEL: Record<string, string> = {
  [CHAT_ACTION_STATUS.CONFIRMED]: '保存しました',
  [CHAT_ACTION_STATUS.CANCELLED]: '取り消しました',
  [CHAT_ACTION_STATUS.EXPIRED]: '期限切れ',
  [CHAT_ACTION_STATUS.FAILED]: '保存できませんでした',
};

/** バックエンドが details.errorCode で返すコード */
export const CHAT_ERROR_CODE = {
  BUDGET_EXCEEDED: 'CHAT_BUDGET_EXCEEDED',
  CONVERSATION_FULL: 'CHAT_CONVERSATION_FULL',
  NOT_FOUND: 'CHAT_NOT_FOUND',
  BUSY: 'CHAT_BUSY',
  ACTION_NOT_FOUND: 'CHAT_ACTION_NOT_FOUND',
  ACTION_ALREADY_HANDLED: 'CHAT_ACTION_ALREADY_HANDLED',
  ACTION_STALE: 'CHAT_ACTION_STALE',
  /** 利用規約・プライバシーポリシーの今の版に同意・確認していない */
  CONSENT_REQUIRED: 'CHAT_CONSENT_REQUIRED',
} as const;

/** 会話をサーバーが保持する日数（backend/src/common/dynamodb_chat.py の CONVERSATION_TTL_DAYS） */
export const CHAT_RETENTION_DAYS = 30;

/** 続きの会話 ID を覚えておく localStorage のキー（ユーザーごと） */
export const chatConversationStorageKey = (userId: string) =>
  `kikakugoke:chat:conversation:${userId}`;

/** 用途（backend/src/handlers/chat/flows/catalog.py の FLOWS の id）のうち、画面が名指しで扱うもの */
export const CHAT_FLOW_ID = {
  MY_SCHEDULE: 'my_schedule',
  HOW_TO: 'how_to',
  DECIDE_DATE: 'decide_date',
  COMPOSE_MESSAGE: 'compose_message',
  REPLY_COMMENT: 'reply_comment',
} as const;

/** 自由入力の会話（用途の ID が無い会話） */
export const CHAT_FREE_FLOW_ID = 'free';

/** 用途の段の種類（ChatFlowStep.kind） */
export const CHAT_STEP_KIND = {
  EVENT: 'event',
  CHOICE: 'choice',
  MULTI: 'multi',
  NUMBER: 'number',
  COMMENT: 'comment',
} as const;

/** 結果の表示データの種類（ChatResultBlock.kind） */
export const CHAT_RESULT_KIND = {
  DATE_RANKING: 'date_ranking',
  SCHEDULE: 'schedule',
  TASKS: 'tasks',
  BUDGET: 'budget',
  SECTIONS: 'sections',
  HELP_ANSWER: 'help_answer',
} as const;

/** 結果の操作ボタン（ChatEntryAction.kind）。押すとサーバーが変更案を組む */
export const CHAT_ENTRY_ACTION = {
  PROPOSE_DATE: 'propose_date',
  PROPOSE_SHARE_TEXT: 'propose_share_text',
  PROPOSE_DESCRIPTION: 'propose_description',
} as const;

/** どこから開いたか（計測用。ChatFlowRunRequest.entry） */
export const CHAT_ENTRY_POINT = {
  HEADER: 'header',
  EVENT_PAGE: 'event_page',
  DASHBOARD: 'dashboard',
  FOLLOWUP: 'followup',
  OTHER: 'other',
} as const;

/**
 * 自由入力の欄に順に出す例（components/chat/ChatInputExamples.tsx）
 *
 * スマホ（375px）でも 1 行に収まるよう、全角 15 字以内にする（chat.test.ts で確かめる）。
 * 汎用の会話でできること（イベントの確認・まとめ・下書き・内容の変更・項目の追加・相談）だけを書く。
 * 公開・削除・招待や、未回答の人の特定（招待した人の名簿は無い）など、できないことは書かない。
 */
export const CHAT_INPUT_EXAMPLES = [
  '来週の飲み会の出欠は？',
  '忘年会の候補日で一番多い日は？',
  '持ち物に炭と紙皿を足して',
  '説明文をもっと丁寧にして',
  '12月の忘年会の下書きを作って',
  'コメントをまとめて',
  '合宿のタイムテーブルを作って',
  '場所を新橋に変えて',
  '定員を20人にして',
  '回答の締切を金曜にして',
  'アンケートの結果は？',
  '今月主催するイベントは？',
  '新年会の案内文を考えて',
  '来月の予定を教えて',
  '買い出しリストを10人分で',
  '会費の集め方を相談したい',
  '雨のときの代わりの案は？',
  '幹事のやることを洗い出して',
  '二次会の店の探し方は？',
  '送別会の企画を一緒に考えて',
  '保留の人への一言を考えて',
  '集合場所の伝え方を考えて',
  '開催レポートの文を書いて',
  '次回の日程の候補を出して',
  '前回と同じ内容で下書きを',
  '社内向けの告知文を作って',
  '子ども連れでも楽しめる案は？',
  '開始を19時に変えて',
  'BBQの持ち物リストを作って',
  '歓迎会の段取りを考えて',
] as const;

/** 入力欄を広げる上限（px。約 5 行。超えたら欄の中でスクロール） */
export const CHAT_INPUT_MAX_HEIGHT_PX = 136;

/** 例を切り替える間隔（ミリ秒） */
export const CHAT_INPUT_EXAMPLE_INTERVAL_MS = 4000;

/** 例の長さの上限（全角で数えた字数。半角は 0.5 字） */
export const CHAT_INPUT_EXAMPLE_MAX_WIDTH = 15;

/** 今月の残りの段階（utils/chatUsage.ts が決める） */
export const CHAT_USAGE_LEVEL = {
  OK: 'ok',
  /** 残りが CHAT_LOW_REMAINING_RATIO 以下 */
  LOW: 'low',
  EXHAUSTED: 'exhausted',
} as const;

export type ChatUsageLevel = (typeof CHAT_USAGE_LEVEL)[keyof typeof CHAT_USAGE_LEVEL];

/** 残りがこれ以下なら「少なくなっています」と知らせる（UsageLimitBanner の警告と同じ 2 割） */
export const CHAT_LOW_REMAINING_RATIO = 0.2;

/** パネルの表示 */
export const CHAT_VIEW = {
  HOME: 'home',
  ALL: 'all',
  STEPS: 'steps',
  HELP: 'help',
  HISTORY: 'history',
  CONVERSATION: 'conversation',
} as const;

/** 「すべて見る」の段階の並び（backend の GROUP_LABELS と同じ id） */
export const CHAT_GROUP_ORDER = ['create', 'collect', 'prepare', 'review', 'personal', 'help'] as const;

/** ホームの「ほかに」に常に出す用途 */
export const CHAT_HOME_SECONDARY_FLOWS = [CHAT_FLOW_ID.MY_SCHEDULE, CHAT_FLOW_ID.HOW_TO] as const;
